import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, ShieldAlert, Loader2, RefreshCw, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { PartFTriageCard } from '@/components/PartFTriageCard';
import { ReturnPremiumCard } from '@/components/ReturnPremiumCard';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const STATUS_COLOR = { met: C.green, late: C.amber, pending: C.slate, amber: C.amber, red: C.red, failed: C.red, legacy: C.blue, not_measured: C.slate };

function Chip({ text, color, testid }) {
  return <span data-testid={testid} className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
    style={{ background: color + '15', color }}>{text}</span>;
}

function PanelCard({ api, panel, isAdmin, onChanged }) {
  const [form, setForm] = useState({ provider: '', contact: '', phone: '', agreement_ref: '', effective_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post('/part-f/panel-agreements', { ...form, service: 'cyber_ir' });
      toast.success('Panel agreement recorded — Cyber binding unblocks from the effective date');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };
  return (
    <Card className="rounded-md bg-white p-4" data-testid="cyber-panel-card"
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${panel?.active ? C.green : C.red}` }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Phone className="w-4 h-4" style={{ color: panel?.active ? C.green : C.red }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>PF-12 · Cyber Incident Response Panel</h3>
        <Chip testid="cyber-panel-status" text={panel?.active ? 'PANEL ON RETAINER — CYBER BINDING OPEN' : 'NO PANEL — CYBER BINDING BLOCKED'} color={panel?.active ? C.green : C.red} />
      </div>
      {panel?.active ? (
        <p className="text-xs" style={{ color: C.navy }}>
          <b>{panel.agreement?.provider}</b> · effective {panel.agreement?.effective_date} · ref {panel.agreement?.agreement_ref || '—'} · {panel.agreement?.contact} {panel.agreement?.phone}
        </p>
      ) : (
        <>
          <p className="text-[11px] mb-2" style={{ color: '#B03230' }}>
            The wording and the Incident Response Card promise specialist response within 4 hours. Without a panel on retainer the promise fails on the first claim of the class — so Cyber will not bind. If a panel is months away, soften the card and the wording together. Both, or neither.
          </p>
          {isAdmin && (
            <div className="grid grid-cols-2 gap-1.5 max-w-xl">
              <Input placeholder="Provider (e.g. CyberCX IR)" value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} className="text-xs rounded-sm" data-testid="panel-provider-input" />
              <Input type="date" value={form.effective_date} onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} className="text-xs rounded-sm" data-testid="panel-date-input" />
              <Input placeholder="24/7 contact" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} className="text-xs rounded-sm" />
              <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="text-xs rounded-sm" />
              <Input placeholder="Agreement reference" value={form.agreement_ref} onChange={e => setForm(f => ({ ...f, agreement_ref: e.target.value }))} className="text-xs rounded-sm" />
              <Button size="sm" onClick={save} disabled={saving || !form.provider.trim()} className="text-xs rounded-sm" style={{ background: C.navy }} data-testid="panel-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record panel agreement'}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export default function PartFPage() {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [reg, setReg] = useState(null);
  const [report, setReport] = useState(null);
  const [filters, setFilters] = useState({ period: '', insurance_class: '', broker: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Promise.all([api.get('/part-f/register'), api.get('/part-f/report', { params })])
      .then(([r1, r2]) => { setReg(r1.data); setReport(r2.data); })
      .catch(() => toast.error('Failed to load Part F register'))
      .finally(() => setLoading(false));
  }, [api, filters]);

  useEffect(() => { load(); }, [load]);

  if (loading && !reg) return <div className="p-8 text-sm" style={{ color: C.slate }}>Loading Part F register…</div>;
  if (!reg) return null;
  const meta = reg.meta || {};

  return (
    <div className="space-y-4" data-testid="part-f-page">
      <div>
        <p className="text-xs" style={{ color: C.slate }}>
          Every timeframe in Part F of a bound wording is a contractual promise — measured against the wording version pinned on each policy, on the insured's own State calendar, amber at 80% of the window.
          Extraction: {meta.extraction_totals?.distinct_timeframes} distinct timeframes · {meta.extraction_totals?.ours} ours · {meta.extraction_totals?.cannot_currently_deliver} not currently deliverable.
          <span className="ml-1 font-medium" style={{ color: C.amber }}>{meta.rows_loaded?.sla_pending ? `${meta.rows_loaded.sla_pending} SLA rows pending file re-upload.` : ''}</span>
        </p>
      </div>

      <PanelCard api={api} panel={reg.cyber_panel} isAdmin={isAdmin} onChanged={load} />

      <ReturnPremiumCard />

      <PartFTriageCard onChanged={load} />

      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="promise-register-table">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>The Promises (measured against wording v{reg.measured_against})</h3>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Promise</th><th className="py-1 pr-2">Window</th><th className="py-1 pr-2">Status</th><th className="py-1">Note</th>
          </tr></thead>
          <tbody>
            {reg.promises.map(p => (
              <tr key={p.id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`promise-row-${p.id}`}>
                <td className="py-1.5 pr-2 font-mono" style={{ color: C.slate }}>{p.seq}</td>
                <td className="py-1.5 pr-2 font-medium" style={{ color: C.navy }}>{p.promise}</td>
                <td className="py-1.5 pr-2" style={{ color: C.navy }}>{p.window ? `${p.window} ${p.unit.replace('_', ' ')}` : 'at event'}</td>
                <td className="py-1.5 pr-2">
                  <Chip text={p.wired ? 'WIRED' : 'BLOCKED'} color={p.wired ? C.green : C.amber} />
                  {p.measured && !p.wired && <Chip text="MEASURED ANYWAY" color={C.blue} />}
                </td>
                <td className="py-1.5 text-[11px]" style={{ color: p.blocked_on ? '#B27516' : C.slate }}>{p.blocked_on || p.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[10px] mt-2" style={{ color: C.slate }}>
          s57 interest: {reg.s57.rate_pct}% pa ({reg.s57.rate_source}) — runs from {reg.s57.reasonable_window_business_days} business days after FNOL, paid unasked at settlement.
        </p>
      </Card>

      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="part-b-table">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>Part B — cover windows &amp; insured obligations (never wired as SLAs)</h3>
        {reg.part_b.map(b => (
          <p key={b.id} className="text-xs py-0.5" style={{ color: C.navy }}>
            <span className="font-mono text-[10px] mr-1" style={{ color: C.slate }}>{b.id}</span>
            {b.name} — {b.window} {b.unit.replace('_', ' ')}
            <span className="ml-1 text-[10px]" style={{ color: b.flag_on_breach ? '#B27516' : C.slate }}>· {b.note}</span>
          </p>
        ))}
      </Card>

      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="part-f-report">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>Promise vs Actual — the capacity-provider view</h3>
          <div className="flex gap-1.5 items-center">
            <Input placeholder="Period YYYY-MM" value={filters.period} onChange={e => setFilters(f => ({ ...f, period: e.target.value }))} className="text-xs rounded-sm h-7 w-28" data-testid="report-period-filter" />
            <Input placeholder="Class" value={filters.insurance_class} onChange={e => setFilters(f => ({ ...f, insurance_class: e.target.value }))} className="text-xs rounded-sm h-7 w-28" data-testid="report-class-filter" />
            <Input placeholder="Broker" value={filters.broker} onChange={e => setFilters(f => ({ ...f, broker: e.target.value }))} className="text-xs rounded-sm h-7 w-28" data-testid="report-broker-filter" />
            <button onClick={load} className="p-1 rounded hover:bg-slate-50" data-testid="report-refresh" style={{ color: C.slate }}><RefreshCw className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        {report && (
          <>
            <div className="flex gap-4 mb-2 flex-wrap">
              <div><p className="text-2xl font-bold" style={{ color: C.navy }} data-testid="report-claims-count">{report.claims_evaluated}</p><p className="text-[10px] uppercase" style={{ color: C.slate }}>claims on the register</p></div>
              <div><p className="text-2xl font-bold" style={{ color: C.blue }} data-testid="report-live-cohort">{(report.cohorts?.live || 0) - (report.excluded_test_artifacts || 0)}</p><p className="text-[10px] uppercase" style={{ color: C.slate }}>live cohort (from {report.measured_from})</p></div>
              {['ok', 'pending', 'amber', 'red'].map(k => (
                <div key={k}><p className="text-2xl font-bold" style={{ color: STATUS_COLOR[k] || C.green }} data-testid={`report-live-${k}`}>{report.live_worst?.[k] || 0}</p><p className="text-[10px] uppercase" style={{ color: C.slate }}>{k === 'ok' ? 'all promises met' : k}</p></div>
              ))}
            </div>
            <p className="text-[11px] mb-3" data-testid="report-legacy-disclosure" style={{ color: C.slate }}>
              <b style={{ color: C.navy }}>{report.legacy?.total || 0} legacy claims</b> sit outside the measured cohort —{' '}
              {report.cohorts?.legacy_closed || 0} closed before the register went live, {report.cohorts?.legacy_pre_go_live || 0} notified before the outbound rail existed.{' '}
              {report.legacy?.needs_explanation || 0} of them raised a flag that needed explaining and {report.legacy?.triaged || 0} carry a signed-off disposition
              {(report.legacy?.no_flag || 0) > 0 ? `; the other ${report.legacy.no_flag} never raised one` : ''}.{' '}
              <span style={{ color: (report.legacy?.untriaged || 0) ? '#B03230' : C.green }}>{report.legacy?.untriaged || 0} still unexplained.</span> {report.met_rate_basis}
            </p>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
                <th className="py-1 pr-2">Promise</th><th className="py-1 pr-2 text-right">Met</th><th className="py-1 pr-2 text-right">Late</th><th className="py-1 pr-2 text-right">Amber</th><th className="py-1 pr-2 text-right">Red</th><th className="py-1 text-right">Met rate</th>
              </tr></thead>
              <tbody>
                {Object.entries(report.promises || {}).map(([id, a]) => (
                  <tr key={id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                    <td className="py-1.5 pr-2" style={{ color: C.navy }}><span className="font-mono text-[10px] mr-1" style={{ color: C.slate }}>{id}</span>{a.promise}</td>
                    <td className="py-1.5 pr-2 text-right" style={{ color: C.green }}>{a.met}</td>
                    <td className="py-1.5 pr-2 text-right" style={{ color: C.amber }}>{a.late}</td>
                    <td className="py-1.5 pr-2 text-right" style={{ color: C.amber }}>{a.amber}</td>
                    <td className="py-1.5 pr-2 text-right" style={{ color: C.red }}>{(a.red || 0) + (a.failed || 0)}</td>
                    <td className="py-1.5 text-right font-medium" style={{ color: C.navy }}>{a.met_rate_pct != null ? `${a.met_rate_pct}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] mt-2" style={{ color: C.slate }}>Measured against each policy's pinned wording, on the insured's State business-day calendar. Amber escalates at 80% of the window — an amber flag prevents a failure, a red flag records one.</p>
          </>
        )}
      </Card>
    </div>
  );
}
